/**
 * XUMM Backend API Client
 * Handles all XUMM-related API calls through our backend server
 */

import { getBackendUrl } from '../config/environments';

const BACKEND_URL = getBackendUrl();

/**
 * Create a XUMM payload through backend
 * @param {Object} payload - Payload data
 * @returns {Promise<Object>} Payload creation result
 */
export const createPayload = async (payload) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/xumm/payload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to create payload');
    }

    return data;
  } catch (error) {
    console.error('Error creating payload:', error);
    throw error;
  }
};

/**
 * Get payload status from backend
 * @param {string} uuid - Payload UUID
 * @returns {Promise<Object>} Payload status
 */
export const getPayloadStatus = async (uuid) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/xumm/payload/${uuid}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to get payload status');
    }

    return data.payload;
  } catch (error) {
    console.error('Error getting payload status:', error);
    throw error;
  }
};

/**
 * Subscribe to payload updates using Server-Sent Events
 * @param {string} uuid - Payload UUID
 * @param {Function} callback - Callback for updates
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToPayload = (uuid, callback) => {
  const eventSource = new EventSource(`${BACKEND_URL}/api/xumm/payload/${uuid}/subscribe`);
  
  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      callback(data);
    } catch (error) {
      console.error('Error parsing SSE data:', error);
    }
  };
  
  eventSource.onerror = (error) => {
    console.error('SSE error:', error);
    eventSource.close();
  };
  
  return {
    unsubscribe: () => {
      eventSource.close();
    }
  };
};

/**
 * Cancel a payload
 * @param {string} uuid - Payload UUID
 * @returns {Promise<boolean>} Success status
 */
export const cancelPayload = async (uuid) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/xumm/payload/${uuid}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Error cancelling payload:', error);
    return false;
  }
};

/**
 * Check backend health
 * @returns {Promise<boolean>} Backend health status
 */
export const checkBackendHealth = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/health`);
    const data = await response.json();
    return data.status === 'ok';
  } catch (error) {
    console.error('Backend health check failed:', error);
    return false;
  }
};

/**
 * Test function to manually simulate a successful sign-in event
 * @param {string} uuid - Payload UUID
 * @param {string} account - Test account address
 * @returns {Promise<Object>} Test result
 */
export const simulateSuccess = async (uuid, account = 'rTestAccount12345678901234567890123456') => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/test/simulate-success`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uuid, account }),
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error simulating success:', error);
    throw error;
  }
}; 